import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import {
  IonButton,
  IonContent,
  IonFooter,
  IonLoading,
  IonPage,
  IonToolbar,
  isPlatform,
  useIonViewWillEnter,
} from '@ionic/react';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { ProfileDetails } from 'src/components/profile/ProfileDetails';
import MobileHeader from 'src/components/reusable-components/MobileHeader';
import { useToast } from 'src/components/reusable-components/useToast';
import { GenericMessages } from 'src/intl/GenericMessages';
import { ProfileMessages } from 'src/intl/ProfileMessages';
import { initiateDeviceAuthFlow, getAccessToken, getAuthorizedUser } from 'src/ble/OAuth2DeviceAuth';

const renderAuthorizeStatus = () => {
  const accessToken = localStorage.getItem('accessToken');
  const authenticatedUser = localStorage.getItem('authenticatedUser');

  if (accessToken && authenticatedUser) {
    return 'Authorized to Sentinel Max (' + authenticatedUser + ')';
  } else {
    return 'Authorize to Sentinel Max';
  }
};

export const MobileProfilePage: React.FC = () => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const [present] = useToast();
  const [buildInfo, setBuildInfo] = useState<{ version: string; build: string } | undefined>(undefined);
  const [authorizedButtonText, setAuthorizedButtonText] = useState(renderAuthorizeStatus);
  const [isAuthorizationInProgress, setIsAuthorizationInProgress] = useState(false);
  const [isStartingAuthorization, setIsStartingAuthorization] = useState(false);

  useIonViewWillEnter(() => {
    setAuthorizedButtonText(renderAuthorizeStatus());

    const exec = async () => {
      try {
        const result = await App.getInfo();
        setBuildInfo({ version: result.version, build: result.build });
      } catch (e) {
        console.warn('Failed to fetch build info', e);
      }
    };
    exec();
  }, []);

  async function authorizeDevice() {
    if (isAuthorizationInProgress) return;

    const verificationWindow = !isPlatform('hybrid') ? window.open('', '_blank') : undefined;
    setIsAuthorizationInProgress(true);
    setIsStartingAuthorization(true);

    try {
      // Initiate device authorization flow
      const mqttGrantType = `${process.env.REACT_APP_MQTT_GRANT_TYPE}`;
      const objResponse1 = await initiateDeviceAuthFlow(mqttGrantType, 'openid offline_access email');
      setIsStartingAuthorization(false);

      if (objResponse1?.verification_uri_complete && objResponse1?.device_code) {
        // Open browser window for user verification
        if (isPlatform('hybrid')) {
          await Browser.open({
            url: objResponse1.verification_uri_complete,
            presentationStyle: 'popover',
            windowName: '_blank',
          });
        } else if (verificationWindow) {
          verificationWindow.location.href = objResponse1.verification_uri_complete;
        } else {
          window.location.href = objResponse1.verification_uri_complete;
        }

        // Start checking for verification result and auth token (10 minutes max)
        console.log(
          `*** Getting Access Token: IntervalSeconds - ${objResponse1.interval} | ExpiresInSeconds -  ${objResponse1.expires_in}`,
        );

        await getAccessToken('urn:ietf:params:oauth:grant-type:device_code', objResponse1.device_code, mqttGrantType, {
          intervalSeconds: objResponse1.interval,
          expiresInSeconds: objResponse1.expires_in,
        });

        // Get the logged on, authenticated, user for the device
        const objResponse2 = await getAuthorizedUser(localStorage.getItem('accessToken') || '');
        if (objResponse2) {
          localStorage.setItem(
            'authenticatedUser',
            objResponse2.givenName.substring(0, 1) + ' ' + objResponse2.familyName,
          );
        }
        setAuthorizedButtonText(renderAuthorizeStatus());
        await present(
          {
            message: formatMessage(ProfileMessages.deviceAuthorizationSuccess),
          },
          'success',
        );
      } else {
        throw new Error('Device authorization request did not return a verification URL.');
      }
    } catch (err: any) {
      verificationWindow?.close();
      console.error('authorizeDevice Error: ', err.message);
      await present(
        {
          message: formatMessage(ProfileMessages.deviceAuthorizationFailure),
        },
        'danger',
      );
    } finally {
      setIsStartingAuthorization(false);
      setIsAuthorizationInProgress(false);
    }
  }

  const isNotAuthorized = authorizedButtonText !== 'Authorize to Sentinel Max';

  return (
    <IonPage className="profile-details-page">
      <MobileHeader title={formatMessage(ProfileMessages.profile)} />
      <IonLoading
        isOpen={isStartingAuthorization}
        message={formatMessage(ProfileMessages.deviceAuthorizationLoading)}
        spinner="circles"
      />
      <IonContent>
        <ProfileDetails buildInfo={buildInfo} />
      </IonContent>
      <IonFooter>
        <IonToolbar>
          <IonButton fill="solid" expand="block" color="primary" onClick={() => history.replace('/logout')}>
            {formatMessage(GenericMessages.logOut)}
          </IonButton>
          <IonButton
            fill="solid"
            expand="block"
            color="primary"
            onClick={authorizeDevice}
            disabled={isAuthorizationInProgress}
            style={{ opacity: isNotAuthorized ? 0.5 : 1 }}
          >
            {authorizedButtonText}
          </IonButton>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};
